import { Component } from '@theme/component';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { normalizeString, onAnimationEnd } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that displays a localization form.
 *
 * @typedef {object} FormRefs
 * @property {HTMLDivElement} countryList - The country list element.
 * @property {HTMLInputElement} countryInput - The country input element.
 * @property {HTMLUListElement[]} countryListItems - The country list items element.
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLDivElement} liveRegion - The live region element.
 * @property {HTMLSelectElement} languageInput - The language input element.
 * @property {HTMLSpanElement} noResultsMessage - The no results message element.
 * @property {HTMLUListElement} popularCountries - The popular countries element.
 * @property {HTMLInputElement} search - The search input element.
 * @property {HTMLButtonElement} resetButton - The reset button element.
 * @property {HTMLSpanElement} searchIcon - The search icon element.
 *
 * @extends {Component<FormRefs>}
 */
class LocalizationFormComponent extends Component {
  /** @type {Element | null} */
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.search && this.refs.search.addEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.addEventListener('keydown', this.#onContainerKeyDown);

    // The modal is rendered once and reopened in place, so a dismissal has to discard whatever
    // the last visit staged. Every dismissal path funnels through `DialogComponent#closeDialog`.
    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener(DialogCloseEvent.eventName, this.resetForm);

    // Resizing the language input can be expensive for browsers that don't support field-sizing: content.
    // Spliting it into separate tasks at least helps when there are multiple localization forms on the page.
    setTimeout(() => this.resizeLanguageInput(), 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.search && this.refs.search.removeEventListener('keydown', this.#onSearchKeyDown);
    this.refs.countryList && this.refs.countryList.removeEventListener('keydown', this.#onContainerKeyDown);
    this.#dialogComponent?.removeEventListener(DialogCloseEvent.eventName, this.resetForm);
  }

  /**
   * Handles the keydown event for the container.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onContainerKeyDown = (event) => {
    const { countryListItems } = this.refs;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('UP');
        break;
      case 'ArrowDown':
        event.preventDefault();
        event.stopPropagation();
        this.#changeCountryFocus('DOWN');
        break;
      case 'Enter': {
        event.preventDefault();
        event.stopPropagation();
        const focusedItem = countryListItems.find((item) => item === document.activeElement);

        if (focusedItem?.dataset.value) {
          this.selectCountry(focusedItem.dataset.value, event);
        }
        break;
      }
    }

    if (!this.refs.search) return;

    setTimeout(() => {
      const focusableItems = this.refs.countryListItems.filter((item) => !item.hasAttribute('hidden'));
      const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
      const focusedItem = focusableItems[focusedItemIndex];

      if (focusedItem) {
        this.refs.search.setAttribute('aria-activedescendant', focusedItem.id);
      } else {
        this.refs.search.setAttribute('aria-activedescendant', '');
      }
    });
  };

  /**
   * Stages a country. The form submits separately, from the Save button.
   *
   * @param {string} isoCode - The ISO code of the country to select.
   * @param {Event} event - The event object.
   */
  selectCountry = (isoCode, event) => {
    event.preventDefault();
    this.#stageCountry(isoCode);
  };

  /**
   * Changes the language of the localization form.
   *
   * @param {Event} event - The event object.
   */
  changeLanguage(event) {
    const { form, languageInput } = this.refs;
    const value = event.target instanceof HTMLSelectElement ? event.target.value : null;

    if (value) {
      languageInput.value = value;
      this.resizeLanguageInput();
      // requestSubmit, not submit: the latter fires no submit event, so market-redirect never
      // learns the shopper made an explicit choice.
      form.requestSubmit();
    }
  }

  resizeLanguageInput() {
    const { languageInput } = this.refs;

    if (!languageInput || CSS.supports('field-sizing', 'content')) return;

    // Hide all options except the selected option
    for (const option of languageInput.options) {
      if (!option.selected) {
        option.dataset.optionLabel = option.textContent || '';
        option.innerText = '';
      }
    }

    // Calculate the width of the select element (which is based on the width of the widest option)
    languageInput.style.width = 'fit-content';
    const originalElementWidth = `${Math.ceil(languageInput.offsetWidth) + 1}px`;

    // Fix the width of the select element
    if (languageInput.offsetWidth > 0) {
      languageInput.style.width = originalElementWidth;
    }

    // Add back all option labels
    for (const option of languageInput.options) {
      if (option.dataset.optionLabel) {
        option.textContent = option.dataset.optionLabel;
        delete option.dataset.optionLabel;
      }
    }
  }

  /**
   * Finds matches for a given search value in a country element.
   *
   * @typedef {Object} Options
   * @property {boolean} [matchLabel] - Whether to match the label.
   * @property {boolean} [matchAlias] - Whether to match the alias.
   * @property {boolean} [matchIso] - Whether to match the iso.
   * @property {boolean} [matchCurrency] - Whether to match the currency.
   * @property {boolean} [labelMatchStart] - Whether to match the label start.
   * @property {boolean} [aliasExactMatch] - Whether to match the alias exact match.
   *
   * @typedef {Object} MatchTypes
   * @property {boolean} [label] - Whether the label matches the search value.
   * @property {boolean} [alias] - Whether the alias matches the search value.
   * @property {boolean} [iso] - Whether the iso matches the search value.
   * @property {boolean} [currency] - Whether the currency matches the search value.
   *
   * @param {string} searchValue - The search value to find matches for.
   * @param {HTMLElement} countryEl - The country element to find matches in.
   * @param {Options} options - The options for the search.
   * @returns {MatchTypes} The matches found in the country element.
   */
  #findMatches(
    searchValue,
    countryEl,
    options = {
      // Which data types (label, alias, iso) to match against
      matchLabel: true,
      matchAlias: true,
      matchIso: true,
      matchCurrency: true,
      // If true, the search value must match the start of the label
      labelMatchStart: false,
      // If true, a result will not display unless the search value equals an alias in its entirety
      aliasExactMatch: false,
    }
  ) {
    let matchTypes = {};
    const { aliases, value: iso } = countryEl.dataset;

    if (options.matchLabel) {
      const countryName = normalizeString(countryEl.querySelector('[data-country-label]')?.textContent ?? '');

      if (!countryName) return matchTypes;

      matchTypes.label = options.labelMatchStart
        ? countryName.startsWith(searchValue)
        : countryName.includes(searchValue);
    }

    if (options.matchCurrency) {
      const currency = normalizeString(countryEl.querySelector('[data-country-currency]')?.textContent ?? '');
      matchTypes.currency = currency.includes(searchValue);
    }

    if (options.matchIso) {
      matchTypes.iso = normalizeString(iso ?? '') == searchValue;
    }

    if (options.matchAlias) {
      const countryAliases = aliases?.split(',').map((alias) => normalizeString(alias));

      if (!countryAliases) return matchTypes;

      matchTypes.alias =
        countryAliases.length > 0 &&
        countryAliases.find((alias) =>
          options.aliasExactMatch ? alias === searchValue : alias.startsWith(searchValue)
        ) !== undefined;
    }

    return matchTypes;
  }

  /**
   * Highlights matching text in a string by wrapping it in <mark> tags.
   *
   * @param {string | null} text - The text to highlight.
   * @param {string} searchValue - The search value to highlight.
   * @returns {string} The text with matching parts wrapped in <mark> tags.
   */
  #highlightMatches(text, searchValue) {
    if (!text || !searchValue) return text ?? '';

    const normalizedText = normalizeString(text);
    const normalizedSearch = normalizeString(searchValue);
    const startIndex = normalizedText.indexOf(normalizedSearch);

    if (startIndex === -1) return text;

    const endIndex = startIndex + normalizedSearch.length;
    const before = text.slice(0, startIndex);
    const match = text.slice(startIndex, endIndex);
    const after = text.slice(endIndex);

    // Upstream marks `before`/`after` and dims them; that reads right on screen but tells
    // assistive tech the non-matching text is the highlight.
    return `${before}<mark>${match}</mark>${after}`;
  }

  /**
   * Filters the countries based on the search value.
   */
  filterCountries() {
    const {
      countryList,
      countryListItems,
      liveRegion,
      noResultsMessage,
      popularCountries,
      resetButton,
      search,
      searchIcon,
    } = this.refs;
    const { labelResultsCount } = this.dataset;
    const searchValue = normalizeString(search.value);
    let countVisibleCountries = 0;

    resetButton.toggleAttribute('hidden', !searchValue);
    searchIcon?.toggleAttribute('hidden', Boolean(searchValue));

    if (popularCountries) {
      popularCountries.toggleAttribute('hidden', Boolean(searchValue));
    }

    for (const countryEl of countryListItems) {
      const countrySpan = countryEl.querySelector('[data-country-label]');

      if (searchValue === '') {
        countryEl.removeAttribute('hidden');
        if (countrySpan) {
          // eslint-disable-next-line no-self-assign
          countrySpan.textContent = countrySpan.textContent;
        }
        countVisibleCountries++;
      } else {
        const matches = this.#findMatches(searchValue, countryEl);

        // In the future, we could reorder/rank filtered results based on the match types
        if (matches.label || matches.alias || matches.iso || matches.currency) {
          countryEl.removeAttribute('hidden');
          if (countrySpan) {
            countrySpan.innerHTML = this.#highlightMatches(countrySpan.textContent, searchValue);
          }
          countVisibleCountries++;
        } else {
          countryEl.setAttribute('hidden', '');
        }
      }
    }

    if (liveRegion && labelResultsCount) {
      liveRegion.innerText = labelResultsCount.replace('[count]', `${countVisibleCountries}`);
    }

    noResultsMessage.hidden = countVisibleCountries > 0;
    countryList.scrollTop = 0;
  }

  /**
   * Changes the focus of the country list items.
   *
   * @param {string} direction - The direction to change the focus.
   */
  #changeCountryFocus(direction) {
    const { countryListItems } = this.refs;
    const focusableItems = countryListItems.filter((item) => !item.hasAttribute('hidden'));
    const focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
    let itemToFocus;

    if (direction === 'UP') {
      itemToFocus =
        focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
    } else {
      itemToFocus =
        focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
    }

    itemToFocus?.focus();
  }

  /**
   * Resets the countries filter.
   *
   * @param {Event} event - The event object.
   */
  resetCountriesFilter(event) {
    const { search } = this.refs;

    event.stopPropagation();
    search.value = '';
    this.filterCountries();
    search.setAttribute('aria-activedescendant', '');
    search.focus();
  }

  /**
   * Handles the keydown event for the search input.
   *
   * @param {KeyboardEvent} event - The event object.
   */
  #onSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    this.#onContainerKeyDown(event);
  };

  /**
   * Discards anything staged since the form was opened: the filter and the pending country.
   */
  resetForm = () => {
    const { search } = this.refs;

    // Read back from the markup rather than a captured field — `countryInput` is hidden, so
    // its `value` *is* its content attribute and staging leaves no default to fall back on.
    this.#stageCountry(this.dataset.currentCountry ?? '');

    if (!search) return;

    if (search.value != '') {
      search.value = '';
      this.filterCountries();
      search.setAttribute('aria-activedescendant', '');
    }
  };

  /**
   * Points the form and the listbox at a country without submitting.
   *
   * @param {string} isoCode - The ISO code of the country to stage.
   */
  #stageCountry(isoCode) {
    const { countryInput, countryListItems } = this.refs;

    if (!countryInput || !countryListItems) return;

    countryInput.value = isoCode;

    for (const item of countryListItems) {
      item.setAttribute('aria-selected', String(item.dataset.value === isoCode));
    }
  }
}

if (!customElements.get('localization-form-component')) {
  customElements.define('localization-form-component', LocalizationFormComponent);
}

/**
 * The country modal's placeholder content. Loads the form from `sections/localization-modal` when
 * a trigger is hovered or focused, or at the latest when the dialog opens, then replaces itself.
 */
class LocalizationModalLoader extends Component {
  /** @type {Promise<void> | null} */
  #loading = null;

  /** @type {Element | null} */
  #dialogComponent = null;

  connectedCallback() {
    super.connectedCallback();
    this.#dialogComponent = this.closest('dialog-component');
    this.#dialogComponent?.addEventListener(DialogOpenEvent.eventName, this.load);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogComponent?.removeEventListener(DialogOpenEvent.eventName, this.load);
  }

  load = () => {
    this.#loading ??= this.#swapInForm();
    return this.#loading;
  };

  async #swapInForm() {
    try {
      // The storefront root keeps the render light; localization comes from the request itself.
      const url = new URL(Theme.routes.root_url, window.location.origin);
      const html = await sectionRenderer.getSectionHTML('localization-modal', true, url);
      const dialog = new DOMParser().parseFromString(html, 'text/html').querySelector('#localization-modal dialog');
      if (!dialog) throw new Error('Localization modal missing from section response');

      // `form 'localization'` sets `return_to` to the URL it rendered for, which here is the
      // section request; saving would land on that bare fragment instead of this page.
      const returnTo = dialog.querySelector('input[name="return_to"]');
      if (returnTo instanceof HTMLInputElement) returnTo.value = window.location.pathname + window.location.search;

      // Laying out ~200 rows mid-entrance made iOS hold the paint until the animation ended.
      const openDialog = this.closest('dialog');
      if (openDialog?.open) await onAnimationEnd(openDialog, undefined, { subtree: false });

      const hadFocus = openDialog?.contains(document.activeElement);
      this.replaceWith(...Array.from(dialog.childNodes, (node) => document.importNode(node, true)));

      // The dialog opened on an empty shell, so hand focus to the search it was waiting for.
      if (hadFocus || document.activeElement === document.body) {
        /** @type {HTMLElement | null} */ (document.querySelector('#localization-modal-search'))?.focus();
      }
    } catch (error) {
      // Allow a later hover, focus or open to retry.
      this.#loading = null;
      console.error(error);
    }
  }
}

if (!customElements.get('localization-modal-loader')) {
  customElements.define('localization-modal-loader', LocalizationModalLoader);
}
